import { useState, type ReactNode } from 'react';
import { Alert, App, Button, Card, Col, Descriptions, Popconfirm, Row, Space, Statistic, Table, Typography } from 'antd';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../../api/client';
import { Can } from '../../rbac/RequirePermission';
import { errMsg } from '../../lib/errors';
import { formatRupees } from '../../lib/money';
import { QueryError } from '../../lib/QueryState';
import { LoadMore } from '../../lib/useCursorPages';
import { formatBps, type PortalSummary } from '../agencies/agencyApi';
import {
  AgencyCommissionsTable,
  AgencyRechargesTable,
  Amounts,
  BalanceCards,
  payoutColumns,
} from '../agencies/agencyViews';
import { useAgencyCommissions, useAgencyPayouts, useAgencyRecharges } from '../agencies/agencyLists';
import { PORTAL_BASE, PORTAL_KEY, usePeopleStats, usePortalSummary } from './portalApi';

/**
 * The agency's own portal (agency logins). Each section is its own sidebar
 * page (`/agency-portal/*`); every call goes to /admin/agency-portal/*, which
 * the API scopes to the signed-in agency — no page ever names an agency id.
 */

/** Page title + the agency's name/code, and the suspended banner. */
export function PortalPage({ title, extra, children }: { title: string; extra?: ReactNode; children: ReactNode }) {
  const query = usePortalSummary();
  const s = query.data;
  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'space-between', alignItems: 'center' }}>
        <Space wrap align="center">
          <Typography.Title level={3} style={{ margin: 0 }}>
            {title}
          </Typography.Title>
          {s && (
            <Typography.Text type="secondary">
              {s.agency.name} · <Typography.Text code copyable>{s.agency.code}</Typography.Text>
            </Typography.Text>
          )}
        </Space>
        {extra}
      </div>
      {s && s.agency.status !== 'active' && (
        <Alert type="error" showIcon message="Your agency is suspended. New recharges earn no commission. Contact the Dude team." />
      )}
      {children}
    </Space>
  );
}

/** `/agency-portal`: this agency's dashboard — its own numbers only. */
export function AgencyPortalPage() {
  const query = usePortalSummary();
  if (query.isError) return <QueryError error={query.error} what="your agency" onRetry={() => void query.refetch()} />;
  const s = query.data;
  return <PortalPage title="Dashboard">{s ? <Overview s={s} /> : <Card loading />}</PortalPage>;
}

function LinkStat({ title, value, to, highlight }: { title: string; value: number | undefined; to: string; highlight?: boolean }) {
  return (
    <Link to={to}>
      <Card size="small" hoverable>
        <Statistic
          title={title}
          value={value ?? '—'}
          valueStyle={highlight && value ? { color: '#d4380d' } : undefined}
        />
      </Card>
    </Link>
  );
}

function Overview({ s }: { s: PortalSummary }) {
  const people = usePeopleStats().data?.people;
  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Row gutter={[16, 16]}>
        <Col xs={12} md={6}>
          <LinkStat title="Listener applications to review" value={people?.listeners.pending} to="/agency-portal/listeners" highlight />
        </Col>
        <Col xs={12} md={6}>
          <LinkStat title="Approved listeners" value={people?.listeners.approved} to="/agency-portal/listeners?status=approved" />
        </Col>
        <Col xs={12} md={6}>
          <LinkStat title="Users" value={people?.users} to="/agency-portal/users" />
        </Col>
        <Col xs={12} md={6}>
          <LinkStat title="Open support requests" value={people?.openTickets} to="/agency-portal/support" highlight />
        </Col>
      </Row>
      <Row gutter={[16, 16]}>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="Everyone who joined with your code" value={s.users} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="Paying users" value={s.payingUsers} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Typography.Text type="secondary">Recharges this month</Typography.Text>
            <div style={{ fontSize: 20 }}>
              <Amounts rows={s.recharges.thisMonth} />
            </div>
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Typography.Text type="secondary">Recharges all time</Typography.Text>
            <div style={{ fontSize: 20 }}>
              <Amounts rows={s.recharges.allTime} />
            </div>
          </Card>
        </Col>
      </Row>
      <BalanceCards balance={s.balance} />
      <Card size="small" title="How your commission works">
        <Descriptions size="small" column={1}>
          <Descriptions.Item label="Your rate">{formatBps(s.commissionBps)}</Descriptions.Item>
          <Descriptions.Item label="Calculated on">
            {s.commissionBase === 'net_of_tax' ? 'Each recharge amount minus GST' : 'Each full recharge amount'}
          </Descriptions.Item>
          <Descriptions.Item label="Hold period">
            {s.holdDays} days after the recharge (refunded recharges are taken back)
          </Descriptions.Item>
          <Descriptions.Item label="Your users join with">
            code <Typography.Text code>{s.agency.code}</Typography.Text> in the app, or the link{' '}
            <Typography.Text copyable>{`https://dudesocial.in/r/${s.agency.code}`}</Typography.Text>
          </Descriptions.Item>
          <Descriptions.Item label="Commission earned">
            <Amounts rows={s.commission.accrued} />
          </Descriptions.Item>
        </Descriptions>
      </Card>
    </Space>
  );
}

export function AgencyPortalRechargesPage() {
  const list = useAgencyRecharges(PORTAL_BASE);
  return (
    <PortalPage title="Recharges">
      {list.isError ? (
        <QueryError error={list.error} what="recharges" onRetry={list.refetch} />
      ) : (
        <AgencyRechargesTable list={list} rows={list.rows} linkUsers={false} />
      )}
    </PortalPage>
  );
}

export function AgencyPortalCommissionPage() {
  const list = useAgencyCommissions(PORTAL_BASE);
  return (
    <PortalPage title="Commission">
      {list.isError ? (
        <QueryError error={list.error} what="commission" onRetry={list.refetch} />
      ) : (
        <AgencyCommissionsTable list={list} rows={list.rows} linkUsers={false} />
      )}
    </PortalPage>
  );
}

export function AgencyPortalPayoutsPage() {
  const query = usePortalSummary();
  if (query.isError) return <QueryError error={query.error} what="your agency" onRetry={() => void query.refetch()} />;
  return <PortalPage title="Payouts">{query.data ? <Payouts s={query.data} /> : <Card loading />}</PortalPage>;
}

function Payouts({ s }: { s: PortalSummary }) {
  const qc = useQueryClient();
  const { message } = App.useApp();
  const list = useAgencyPayouts(`${PORTAL_BASE}/payouts`);
  // One key per request attempt: a double click / retry returns the same payout.
  const [key, setKey] = useState(() => crypto.randomUUID());
  const request = useMutation({
    mutationFn: () => api.post(`${PORTAL_BASE}/payouts`, {}, { headers: { 'idempotency-key': key } }),
    onSuccess: () => {
      message.success('Payout requested — the Dude team will review it');
      setKey(crypto.randomUUID());
      void qc.invalidateQueries({ queryKey: PORTAL_KEY });
      void qc.invalidateQueries({ queryKey: ['admin', 'agency-lists'] });
    },
    onError: (e) => message.error(errMsg(e, 'Could not request the payout')),
  });
  const b = s.balance;
  const hasDetails = !!s.agency.payoutDetails.payoutMethod;
  const canRequest =
    s.canRequestPayout && hasDetails && !b.openPayoutId && Number(b.payablePaise) >= Number(b.minPayoutPaise) && Number(b.payablePaise) > 0;
  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <BalanceCards balance={b} />
      {!hasDetails && <Alert type="warning" showIcon message="Your payout details are not set yet. Ask the Dude team to add your bank account or UPI ID." />}
      {s.canRequestPayout ? (
        <Can permission="agency_portal.request_payout">
          <Popconfirm title={`Request a payout of ${formatRupees(b.payablePaise)}?`} onConfirm={() => request.mutate()} disabled={!canRequest}>
            <Button type="primary" disabled={!canRequest} loading={request.isPending}>
              Request payout of {formatRupees(b.payablePaise)}
            </Button>
          </Popconfirm>
          {b.openPayoutId && (
            <Typography.Text type="secondary" style={{ marginLeft: 12 }}>
              A payout is already in progress.
            </Typography.Text>
          )}
        </Can>
      ) : (
        <Typography.Text type="secondary">Payouts are arranged by the Dude team.</Typography.Text>
      )}
      {list.isError ? (
        <QueryError error={list.error} what="payouts" onRetry={list.refetch} />
      ) : (
        <>
          <Table rowKey="id" size="small" loading={list.isLoading} columns={payoutColumns()} dataSource={list.rows} pagination={false} scroll={{ x: 900 }} />
          <LoadMore list={list} noun="payouts" />
        </>
      )}
    </Space>
  );
}
